import { useCallback, useState } from 'react';

/** Ни один блок не показан целиком — общая ссылка на пустой набор, чтобы сброс не давал лишнего рендера. */
const NO_SHOWN_BLOCKS: ReadonlySet<number> = new Set();

/** Блоки заключения, показанные целиком вопреки действующему отбору. */
export type FullyShownBlocks = {
  /** Идентификаторы блоков, показанных целиком */
  blockIds: ReadonlySet<number>;
  /** Блок, раскрытый последним действием; `null` — последнее действие вернуло блок к отобранному виду */
  shownBlockId: number | null;
  /** Показывает блок целиком, а показанный — возвращает к отобранному виду */
  toggleBlock: (blockId: number) => void;
};

/** Раскрытые блоки вместе с тем, какой из них раскрыт последним действием. */
type FullViewState = {
  blockIds: ReadonlySet<number>;
  shownBlockId: number | null;
};

const NO_FULL_VIEW: FullViewState = { blockIds: NO_SHOWN_BLOCKS, shownBlockId: null };

/**
 * Набор блоков заключения, раскрытых пользователем вопреки отбору.
 *
 * Полный показ принадлежит разбору одного заключения при одном отборе и нигде не хранится: смена
 * `resetKey` (другой диалог, другое заключение, другой отбор) очищает набор, поэтому заключение
 * открывается заново отобранным. Сброс идёт прямо в рендере — иначе раскрытые блоки успели бы
 * показаться под новым отбором.
 *
 * Раскрытый последним действием блок выделен отдельно: к нему заключение прокручивается. Возврат
 * блока к отобранному виду и сброс набора его обнуляют — прокрутка остаётся там, где была.
 */
export const useFullyShownBlocks = (resetKey: string): FullyShownBlocks => {
  const [fullView, setFullView] = useState<FullViewState>(NO_FULL_VIEW);
  const [shownForKey, setShownForKey] = useState(resetKey);

  if (shownForKey !== resetKey) {
    setShownForKey(resetKey);
    setFullView(NO_FULL_VIEW);
  }

  const toggleBlock = useCallback((blockId: number) => {
    setFullView(({ blockIds }) => {
      const next = new Set(blockIds);
      if (next.delete(blockId)) return { blockIds: next, shownBlockId: null };
      next.add(blockId);
      return { blockIds: next, shownBlockId: blockId };
    });
  }, []);

  return { blockIds: fullView.blockIds, shownBlockId: fullView.shownBlockId, toggleBlock };
};
